import { makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import ListIcon from '@material-ui/icons/List'
import ConvertIcon from '@material-ui/icons/SwapHoriz'
import get from 'lodash/get'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getFeatures, getItems, getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'
import HideAmountButton from './HideAmountButton'
import ItemRowContent from './ItemRowContent'
import OptionsConsumerToggle from './OptionsConsumerToggle'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'
import WorkOrderOptionRowContent from './WorkOrderOptionRowContent'

export interface OptionsTableNextProps {
  collapsed?: boolean
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction: (action: string, section: 'options', item: RenderableItem) => void
  onToggle?: (ev: React.MouseEvent) => any
  showAddButton?: boolean
  inPresentation?: boolean
  /**
   * Renders inside the Work Order tab: adds the Customer/Price vs Crew/Hours display switcher
   * above the card (mirrors the legacy `WorkOrderView`'s `inWorkOrder` prop).
   */
  inWorkOrder?: boolean
  /** Current Work Order Options display (`quote.options.workOrderOptionsConsumer`). Ignored unless `inWorkOrder`. */
  consumer?: 'crew' | 'customer'
  onConsumerChange?: (value: 'crew' | 'customer') => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      // The Customer/Price vs Crew/Hours switcher sits in its own right-aligned row above the card
      // (not in the section strip, which is reserved for the title + Hide toggle).
      consumerToggleRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(1),
        '@media print': {
          display: 'none'
        }
      },
      hidePrint: {
        '@media print': {
          display: 'none'
        }
      }
    }
  },
  { name: 'OptionsTableNext' }
)

/**
 * The "Options" section for the new (estimateNext) pipeline, ported from `OptionsTable` onto the
 * shared outlined primitives. A single "solo" header strip (grey + skinny) holds the section title,
 * the select-all checkbox, and the Hide toggle — there's no separate "Item" column band.
 */
export default function OptionsTableNext({
  collapsed,
  selectedItems,
  onItemSelection,
  onItemAction,
  onToggle,
  showAddButton,
  inWorkOrder,
  consumer: consumerProp,
  onConsumerChange
}: OptionsTableNextProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const { isCrew, isOfficeUser } = useUser()
  const { permissionToWriteAllDocuments, permissionToEditAccountSettings } = useUserPermissions()
  const { quote, tableView, onReorder, onAddItem, isEditable, canAddAdditionalWork, isCustomerView } = useQuote()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const features = getFeatures({ options })
  const hasAdditionalWork = get(features, 'quotes.additionalWork')
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const hasOptionsApproval = options?.options?.optionsApproval?.enabled

  const { addIndex, hideAllPrices, showWorkOrderAreaOptionValues, showWorkOrderSubstrateOptionValues } =
    getQuoteOptions({ quote, options })
  const { allowQuoteAdditionalWork } = getQuoteOptions({ options })

  // Only the Work Order tab offers the Customer/Price vs Crew/Hours switch — the estimate's Options
  // card is always the customer-facing one.
  const consumer = inWorkOrder ? consumerProp ?? 'customer' : 'customer'
  const showHours = tableView === 'substrate' ? showWorkOrderSubstrateOptionValues : showWorkOrderAreaOptionValues
  // The Crew/Hours view renders each option like an Area/Substrate card (rows + a Total bar) instead
  // of a plain priced row (ENG-1850).
  const isCrewView = !!inWorkOrder && consumer === 'crew'

  // Both footer labels are account-configurable, so they're user content — translated the same way
  // the cards translate theirs (the hook batches across instances).
  const [translatedLabels] = useTranslatedNoteContent([objectLabels.prep.value, objectLabels.labour.value])
  const translatedPrep = translateStructuredWorkOrderContent(translatedLabels[0] || objectLabels.prep.value, __)
  const translatedLabour = translateStructuredWorkOrderContent(translatedLabels[1] || objectLabels.labour.value, __)

  const items: RenderableItem[] = useMemo(
    () =>
      getItems({
        quote,
        section: 'options',
        view: tableView,
        consumer,
        options,
        overrideOptions: consumer === 'crew' ? { showQuantities: true } : { showPrices: true },
        showValues: consumer === 'crew' ? showHours : undefined
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView, options, consumer, showHours]
  )

  if (items.length === 0 && !canAddAdditionalWork) {
    return null
  }

  function handleReorder({ items, addIndex }) {
    onReorder({ items, section: 'options', view: tableView, addIndex })
  }

  const canShowAddButton = canAddAdditionalWork && showAddButton
  const useCheckboxes = !!(canAddAdditionalWork && onItemSelection)
  const showAmount = consumer === 'crew' ? showHours : !hideAllPrices

  // Customer-facing nudge only — in the editor the estimator already knows, and the strip there has
  // to leave room for the Hide toggle.
  const sectionSubtitle = isCustomerView
    ? __('Select items below to add to your %s', objectLabels.quote.value.toLowerCase())
    : undefined

  // Nothing to hide until the section holds an option (ENG-1478).
  const showHideToggle = canAddAdditionalWork && items.length > 0

  return (
    <div className={classes.root}>
      {inWorkOrder && isEditable && onConsumerChange && (
        <div className={classes.consumerToggleRow}>
          <OptionsConsumerToggle value={consumer} onChange={onConsumerChange} />
        </div>
      )}
      <OutlinedItemTable
        sectionTitle={objectLabels.option.plural}
        sectionSubtitle={sectionSubtitle}
        largeSectionTitle={isCustomerView}
        sectionAction={
          showHideToggle ? (
            <HideAmountButton visible={!collapsed} visibleLabel="Hide" hiddenLabel="Show" onToggle={onToggle} />
          ) : undefined
        }
        collapsed={collapsed}
        hideHeader
        insetRowDividers={isCrewView}
        draggable={!!isEditable}
        checkboxes={useCheckboxes}
        selectedItems={selectedItems}
        items={items}
        addIndex={addIndex.options}
        addButtonLabel={`Add ${objectLabels.option.value}`}
        showAddButton={canShowAddButton}
        section={'options'}
        onItemSelection={onItemSelection}
        onItemClick={
          (!quote.is_invoice || permissionToWriteAllDocuments) && canAddAdditionalWork
            ? (_ev, item) => onItemAction('edit', 'options', item as RenderableItem)
            : undefined
        }
        singleBorder
        onAddClick={(_ev, insertAfterKey, type) => onAddItem('options', insertAfterKey, type)}
        onReorder={handleReorder}
        renderRowColumns={(item: RenderableItem) => {
          const actions = []
          actions.push({ key: 'edit', label: 'Edit', icon: EditIcon })
          if ((!isCrew && !isOfficeUser && !quote.is_invoice) || !hasAdditionalWork) {
            actions.push({ key: 'move-to-bid', label: `Move to ${objectLabels.quote.value}`, icon: AddBoxIcon })
          }
          if (!quote.is_invoice && hasAdditionalWork && allowQuoteAdditionalWork) {
            actions.push({ key: 'move-to-pending', label: `Move to Additional Work`, icon: AddBoxIcon })
          }
          if (quote.is_invoice && hasAdditionalWork) {
            actions.push({
              key: 'move-to-pending',
              label: `Move to ${objectLabels.additionalWork.value}`,
              icon: AddBoxIcon
            })
          }
          if (!quote.is_invoice && hasOptionsApproval) {
            actions.push({ key: 'move-to-added-options', label: `Move to Added Options`, icon: AddBoxIcon })
          }
          // Duplicate sits above Archive, matching the Item table and the prototype (ENG-1922).
          if (tableView === 'area' && item.type !== 'group') {
            actions.push({ key: 'copy', label: 'Duplicate', icon: CopyIcon })
          }
          actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })
          if (canConvertToLineItem && item.type !== 'line_item') {
            actions.push({ key: 'convert', label: 'Convert to Line Item', icon: ConvertIcon })
          }
          if (canSaveItemToSettings({ permissionToEditAccountSettings, item })) {
            actions.push(saveToSettingsAction)
          }
          if (item.type === 'group') {
            actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
          }

          // Last, as the one destructive action — the prototype puts Delete at the foot of every
          // item menu (ENG-1922). The "Are you sure?" prompt comes from the context:
          // `handleItemAction('delete')` opens a ConfirmationDialog before removing anything.
          //
          // Not in the crew work-order view: the prototype's menu is the estimate editor's, and
          // permanently removing an option off the document is not a painter's action.
          if (!isCrewView) {
            actions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })
          }

          // The Crew/Hours view embeds the hour card's body (crew description, products, notes,
          // Coats) instead of the priced row, so an option reads like the cards above it.
          return isCrewView ? (
            <WorkOrderOptionRowContent
              item={item}
              showHours={showAmount}
              prepLabel={translatedPrep}
              labourLabel={translatedLabour}
              actions={canAddAdditionalWork ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'options', it)}
            />
          ) : (
            <ItemRowContent
              item={item}
              section={'options'}
              isEditable={canAddAdditionalWork}
              showAmount={showAmount}
              consumer={consumer}
              compact={inWorkOrder}
              actions={canAddAdditionalWork ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'options', it)}
            />
          )
        }}
      />
    </div>
  )
}
